import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, Sparkles, X } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { CARRIER_TYPES } from "@shared/schemas/business";
import { US_STATES } from "@/lib/usStates";
import { cn } from "@/lib/utils";

/**
 * Carrier-picker for the migration wizard's plan edit form. Replaces the
 * free-text carrier `<Input>` with a structured flow:
 *
 *   - Auto-suggests matches from the global `carriers` catalog using
 *     pg_trgm similarity against the parsed carrier name. Top match at
 *     similarity >= 0.7 surfaces as a "Did you mean?" banner; broker
 *     one-clicks to link.
 *   - "Browse all" opens a searchable Command palette of every carrier.
 *   - "Create new" opens a small dialog requiring at least one type
 *     and one available state, then POSTs `/api/carriers` and auto-
 *     links the new record.
 *
 * Caller owns persistence: `onChange({ id, name })` fires whenever
 * the broker confirms a selection or types a new free-text value.
 */

interface Carrier {
  id: number;
  name: string;
  types?: string[] | null;
  availableStates?: string[] | null;
}

interface MatchCandidate extends Carrier {
  similarity: number;
}

interface Props {
  carrierName: string;
  carrierId: number | null;
  onChange: (next: { id: number | null; name: string }) => void;
  required?: boolean;
}

const SUGGEST_THRESHOLD = 0.7;

export function CarrierPicker({
  carrierName,
  carrierId,
  onChange,
  required,
}: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [browseOpen, setBrowseOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  // Global carriers list. Used for the "Browse all" picker + the
  // linked-state name lookup if the parent only has the id.
  const { data: allCarriers = [] } = useQuery<Carrier[]>({
    queryKey: ["/api/carriers"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/carriers");
      return (await res.json()) as Carrier[];
    },
  });

  // Trigram match against the typed/parsed carrierName. Debounced via
  // a request-id ref so fast typing doesn't show out-of-order results.
  //
  // Exact (case-insensitive) name match auto-links — no point asking
  // "did you mean Kaiser Permanente?" when the broker typed exactly
  // "Kaiser Permanente". Anything below an exact match falls through
  // to the "did you mean?" suggestion banner.
  const [matches, setMatches] = useState<MatchCandidate[]>([]);
  const requestIdRef = useRef(0);
  useEffect(() => {
    if (carrierId != null) {
      // Already linked — no need to suggest alternatives.
      setMatches([]);
      return;
    }
    const query = carrierName.trim();
    if (!query) {
      setMatches([]);
      return;
    }
    // Cheap local exact-match check first against the already-loaded
    // carriers list. Skips the network roundtrip entirely when it's a
    // clear hit. Falls through to /match when the name needs fuzzy.
    const exactLocal = allCarriers.find(
      (c) => c.name.trim().toLowerCase() === query.toLowerCase(),
    );
    if (exactLocal) {
      onChange({ id: exactLocal.id, name: exactLocal.name });
      setMatches([]);
      return;
    }
    const myId = ++requestIdRef.current;
    const handle = window.setTimeout(async () => {
      try {
        const res = await apiRequest("POST", "/api/carriers/match", {
          name: query,
        });
        const data = (await res.json()) as { matches: MatchCandidate[] };
        if (myId !== requestIdRef.current) return;
        const exactRemote = (data.matches ?? []).find(
          (m) => m.name.trim().toLowerCase() === query.toLowerCase(),
        );
        if (exactRemote) {
          // Carrier added since the /carriers list was fetched — link
          // it immediately rather than surface a redundant suggestion.
          onChange({ id: exactRemote.id, name: exactRemote.name });
          setMatches([]);
          return;
        }
        setMatches(data.matches ?? []);
      } catch {
        if (myId === requestIdRef.current) setMatches([]);
      }
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierName, carrierId, allCarriers]);

  const linkedCarrier = useMemo(
    () =>
      carrierId != null
        ? allCarriers.find((c) => c.id === carrierId) ?? null
        : null,
    [allCarriers, carrierId],
  );
  const topMatch = matches[0];
  const hasSuggestion =
    !linkedCarrier && topMatch && topMatch.similarity >= SUGGEST_THRESHOLD;

  // ── Render branches ──────────────────────────────────────────────

  if (linkedCarrier) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex flex-1 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[12.5px] text-emerald-900">
          <Check className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
          <span className="truncate font-medium">
            Linked · {linkedCarrier.name}
          </span>
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 text-[11.5px] text-stone-600 hover:text-stone-900"
          onClick={() => onChange({ id: null, name: linkedCarrier.name })}
        >
          <X className="mr-1 h-3 w-3" />
          Change
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Input
        className={cn(
          "h-8 text-sm",
          required && !carrierName.trim() && "border-amber-400 bg-amber-50",
        )}
        value={carrierName}
        onChange={(e) => onChange({ id: null, name: e.target.value })}
        placeholder="Carrier name"
      />
      {hasSuggestion ? (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-900">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-700" />
          <span className="flex-1 min-w-0">
            Did you mean{" "}
            <strong className="font-semibold">{topMatch.name}</strong>?
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 border-amber-400 px-2 text-[11px] text-amber-900 hover:bg-amber-100/70"
            onClick={() => onChange({ id: topMatch.id, name: topMatch.name })}
          >
            Yes, link it
          </Button>
          <button
            type="button"
            onClick={() => setBrowseOpen(true)}
            className="text-[11px] font-medium text-amber-800 underline-offset-2 hover:underline"
          >
            Browse all
          </button>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="text-[11px] font-medium text-amber-800 underline-offset-2 hover:underline"
          >
            Create new
          </button>
        </div>
      ) : (
        carrierName.trim() && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-stone-200 bg-stone-50 px-2.5 py-1.5 text-[11.5px] text-stone-600">
            <span className="flex-1 min-w-0">
              No close match for "<strong>{carrierName.trim()}</strong>".
            </span>
            <button
              type="button"
              onClick={() => setBrowseOpen(true)}
              className="text-[11px] font-medium text-stone-700 underline-offset-2 hover:underline"
            >
              Browse all
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="text-[11px] font-medium text-stone-700 underline-offset-2 hover:underline"
            >
              Create new
            </button>
          </div>
        )
      )}

      {/* ── Browse-all dialog ─────────────────────────────────── */}
      <Dialog open={browseOpen} onOpenChange={setBrowseOpen}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-[480px]">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="text-sm">All carriers</DialogTitle>
            <DialogDescription className="text-[11.5px]">
              Pick the carrier from the global catalog.
            </DialogDescription>
          </DialogHeader>
          <Command className="border-t">
            <CommandInput placeholder="Search carriers…" />
            <CommandList>
              <CommandEmpty>No carriers found.</CommandEmpty>
              <CommandGroup>
                {allCarriers.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.name}
                    onSelect={() => {
                      onChange({ id: c.id, name: c.name });
                      setBrowseOpen(false);
                    }}
                  >
                    <div className="flex flex-col">
                      <span className="text-[12.5px] font-medium">
                        {c.name}
                      </span>
                      {c.types && c.types.length > 0 && (
                        <span className="text-[10.5px] text-stone-500">
                          {c.types.join(" · ")}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      {/* ── Create-new dialog ─────────────────────────────────── */}
      <CreateCarrierDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        initialName={carrierName.trim()}
        onCreated={(c) => {
          qc.invalidateQueries({ queryKey: ["/api/carriers"] });
          onChange({ id: c.id, name: c.name });
          toast({ title: "Carrier created" });
        }}
      />
    </div>
  );
}

// ── Create dialog ──────────────────────────────────────────────────

function CreateCarrierDialog({
  open,
  onOpenChange,
  initialName,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialName: string;
  onCreated: (c: Carrier) => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(initialName);
  const [types, setTypes] = useState<Set<string>>(new Set());
  const [states, setStates] = useState<Set<string>>(new Set());

  // Reset form when dialog opens with a different initial name.
  useEffect(() => {
    if (open) {
      setName(initialName);
      setTypes(new Set());
      setStates(new Set());
    }
  }, [open, initialName]);

  const toggle = (
    set: Set<string>,
    setter: (s: Set<string>) => void,
    value: string,
  ) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setter(next);
  };

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/carriers", {
        name: name.trim(),
        types: Array.from(types),
        availableStates: states.size > 0 ? Array.from(states) : null,
        isActive: true,
      });
      return (await res.json()) as Carrier;
    },
    onSuccess: (carrier) => {
      onCreated(carrier);
      onOpenChange(false);
    },
    onError: (err: any) =>
      toast({
        title: "Couldn't create carrier",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  const canCreate = name.trim().length > 0 && types.size > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Create new carrier</DialogTitle>
          <DialogDescription>
            Adds a new entry to the global carrier catalog. It'll be
            immediately available to all agencies.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label className="text-xs">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Anthem Blue Cross"
              className="h-9 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Coverage types <span className="text-red-500">*</span>
            </Label>
            <div className="flex flex-wrap gap-1.5">
              {CARRIER_TYPES.map((t) => {
                const selected = types.has(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggle(types, setTypes, t)}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-[11.5px] font-medium",
                      selected
                        ? "border-stone-900 bg-stone-900 text-white"
                        : "border-stone-300 bg-white text-stone-700 hover:bg-stone-50",
                    )}
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">
              Available states{" "}
              <span className="text-stone-400">(leave empty for all)</span>
            </Label>
            <div className="max-h-[160px] overflow-y-auto rounded-md border border-stone-200 px-2 py-1.5">
              <div className="flex flex-wrap gap-1.5">
                {US_STATES.map((s) => {
                  const selected = states.has(s.code);
                  return (
                    <button
                      key={s.code}
                      type="button"
                      onClick={() => toggle(states, setStates, s.code)}
                      className={cn(
                        "rounded border px-1.5 py-0.5 text-[10.5px] font-mono font-medium",
                        selected
                          ? "border-stone-900 bg-stone-900 text-white"
                          : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50",
                      )}
                      title={s.name}
                    >
                      {s.code}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => create.mutate()}
            disabled={!canCreate || create.isPending}
          >
            {create.isPending ? "Creating…" : "Create carrier"}
            {!create.isPending && <Plus className="ml-1 h-3.5 w-3.5" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

